/**
 * Run Stream Service
 *
 * Singleton bridge between the runStreamWorker (Web Worker)
 * and the runStore (Zustand). Manages worker lifecycle,
 * message routing, and reconnection with exponential backoff.
 *
 * @module shared/workers/RunStreamService
 */

import { useRunStore } from '../../store/automation/runStore';
import { useUserStore } from '../../store/user/userStore';
import logger from '../services/logging/logger';
import { resolveRunCounts } from '../utils/permutationStatus';
import { createRunStreamWorker } from './runStreamWorkerFactory';

const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 15000];
const STALE_RUN_THRESHOLD_MS = 2 * 60 * 60 * 1000; // 2 hours
const STALE_MESSAGE_TIMEOUT_MS = 15_000; // 15s without a message → poll REST
const STALE_POLL_DELAYS = [15_000, 30_000, 30_000, 60_000, 60_000]; // backoff for subsequent polls
const MAX_STALE_POLLS = 5; // stop polling after this many consecutive stale checks

/**
 * Normalize a REST API run response so it has the
 * testResults.testSummary.runStatus = 'COMPLETED' structure
 * that the UI selectors (useIsCompleted, useIsRunning, etc.) expect.
 * WebSocket payloads already have this shape; REST responses may
 * only have a top-level `result` field (PASSED/FAILED/ERROR).
 */
const normalizeCompletedRunState = (run) => {
  if (run?.testResults?.testSummary?.runStatus === 'COMPLETED' &&
      Array.isArray(run.testResults.permutations)) return run;
  return {
    ...run,
    testResults: {
      ...(run.testResults || {}),
      permutations: run.testResults?.permutations || [],
      testSummary: {
        ...(run.testResults?.testSummary || {}),
        runStatus: 'COMPLETED',
        runProgressPercentage: 100,
      },
    },
  };
};

/**
 * Merge testSummary count fields from the current (progress) state into an
 * incoming payload. The backend may send a COMPLETED message with zeroed-out
 * counts while the preceding progress messages had the correct tallies.
 * For each count field we keep Math.max(current, incoming) so real results
 * are never overwritten by a late reset.
 */
const SUMMARY_COUNT_FIELDS = [
  'numberOfTests', 'numberOfTestsPassed', 'numberOfTestsFailed',
  'numberOfPermutations', 'numberOfPermutationsExecuted',
  'numberOfPermutationsPassed', 'numberOfPermutationsFailed',
  'numberOfPermutationsSkipped', 'numberOfPermutationsDisabled',
];

const mergeRunSummary = (currentState, incomingPayload) => {
  const curSummary = currentState?.testResults?.testSummary;
  const incSummary = incomingPayload?.testResults?.testSummary;
  if (!curSummary || !incSummary) return incomingPayload;

  // Merge testSummary count fields — keep the higher value so earlier progress
  // tallies aren't wiped by a late message with zeroed counts.
  const mergedCounts = {};
  let changed = false;
  for (const field of SUMMARY_COUNT_FIELDS) {
    const cur = curSummary[field] ?? 0;
    const inc = incSummary[field] ?? 0;
    if (cur > inc) {
      mergedCounts[field] = cur;
      changed = true;
    }
  }

  if (!changed) return incomingPayload;

  return {
    ...incomingPayload,
    testResults: {
      ...incomingPayload.testResults,
      testSummary: {
        ...incSummary,
        ...mergedCounts,
      },
    },
  };
};

class RunStreamService {
  constructor() {
    this._worker = null;
    this._reconnectTimer = null;
    this._reconnectAttempts = 0;
    this._staleTimer = null;
    this._stalePollCount = 0; // tracks consecutive stale polls without WS messages
    this._generation = 0; // incremented on each new worker — filters stale messages
    this._wsMessageCount = 0; // tracks WS messages received in current run
    this._connectId = 0; // incremented on connect/disconnect — guards async snapshot fetch
    this._lastSeq = new Map(); // runId -> highest WS frame `sequence` applied (frame ordering)
    this._terminal = new Set(); // runIds that have completed — later stragglers are dropped
  }

  // ── Worker Lifecycle ────────────────────────────────────────────

  /**
   * Create the Web Worker lazily and wire up message handling.
   */
  _ensureWorker() {
    if (this._worker) return;

    this._generation++;
    const gen = this._generation;

    this._worker = createRunStreamWorker();

    // Capture generation — stale messages from a previous worker are ignored
    this._worker.onmessage = (event) => {
      if (gen !== this._generation) return;
      this._handleWorkerMessage(event);
    };

    this._worker.onerror = (err) => {
      if (gen !== this._generation) return;
      logger.error('RunStreamService: worker error', { error: err.message });
      this._destroyWorker();

      // Attempt reconnection if a run is active
      const { activeRunMeta } = useRunStore.getState();
      if (activeRunMeta) {
        this._scheduleReconnect();
      }
    };
  }

  /**
   * Terminate the worker and clear references.
   */
  _destroyWorker() {
    if (this._worker) {
      this._worker.onmessage = null;
      this._worker.onerror = null;
      this._worker.terminate();
      this._worker = null;
    }
  }

  // ── Stale Message Timeout ───────────────────────────────────────

  /**
   * Reset the stale-message timer. Called every time a WS_MESSAGE or
   * WS_COMPLETED arrives. If no message arrives within STALE_MESSAGE_TIMEOUT_MS
   * while a run is still IN_PROGRESS, we poll REST for the final status.
   */
  _resetStaleTimer() {
    this._clearStaleTimer();
    this._stalePollCount = 0; // WS message arrived — reset backoff
    this._staleTimer = setTimeout(() => {
      this._staleTimer = null;
      this._handleStaleRun();
    }, STALE_MESSAGE_TIMEOUT_MS);
  }

  /**
   * Schedule the next stale poll with backoff. Used after a REST poll
   * confirms the run is still in progress (no WS message arrived).
   */
  _scheduleNextStalePoll() {
    this._clearStaleTimer();
    const delay = STALE_POLL_DELAYS[this._stalePollCount - 1] || STALE_POLL_DELAYS[STALE_POLL_DELAYS.length - 1];
    this._staleTimer = setTimeout(() => {
      this._staleTimer = null;
      this._handleStaleRun();
    }, delay);
  }

  _clearStaleTimer() {
    if (this._staleTimer) {
      clearTimeout(this._staleTimer);
      this._staleTimer = null;
    }
  }

  /**
   * No messages received for STALE_MESSAGE_TIMEOUT_MS.
   * Check via REST whether the run already completed.
   */
  async _handleStaleRun() {
    const store = useRunStore.getState();
    if (!store.activeRunMeta) return;

    this._stalePollCount++;
    const { activeRunMeta } = store;

    // After max attempts, do one final REST check — the run may have completed
    // between our last poll and now. If still running, schedule a slow background poll.
    if (this._stalePollCount > MAX_STALE_POLLS) {
      logger.warn('RunStreamService: max stale polls reached, doing final completion check', {
        runId: activeRunMeta.runId,
        polls: this._stalePollCount,
      });
      await this._finalCompletionCheck(activeRunMeta);
      return;
    }

    logger.info('RunStreamService: no WS messages, polling REST for status', {
      runId: activeRunMeta.runId,
      poll: this._stalePollCount,
    });

    try {
      const { TestService } = await import('../middleware/services/TestService');
      const userData = useUserStore.getState().userData;
      const run = await TestService.getRun(
        activeRunMeta.projectId,
        activeRunMeta.runnableId,
        activeRunMeta.runId,
        userData,
        activeRunMeta.tenantId,
        { bypassCache: true }
      );

      if (run?.testResults?.testSummary?.runStatus === 'COMPLETED' ||
          run?.result === 'PASSED' || run?.result === 'FAILED' || run?.result === 'ERROR') {
        logger.info('RunStreamService: REST confirms run completed (missed via WS)');

        const current = useRunStore.getState().runState;
        const normalized = normalizeCompletedRunState(run);
        const currentPerms = current?.testResults?.permutations?.length || 0;
        const restPerms = normalized.testResults?.permutations?.length || 0;

        if (restPerms > currentPerms) {
          // REST has more data — merge counts so WS tallies aren't lost
          useRunStore.getState().setRunState(mergeRunSummary(current, normalized));
        } else {
          // Keep WS data (richer), but mark it as COMPLETED
          useRunStore.getState().setRunState(normalizeCompletedRunState(current));
        }

        useRunStore.getState().completeRun();
        this._destroyWorker();
        return;
      }

      // Still running — update workerStatus so UI shows "Running" instead of "Queuing"
      const currentStatus = useRunStore.getState().workerStatus;
      if (currentStatus !== 'streaming') {
        useRunStore.getState().setWorkerStatus('streaming');
      }
      // If REST returned progress data, merge into run state so WS-streamed
      // steps aren't wiped by a REST poll response with an empty permutations array.
      if (run?.testResults?.testSummary) {
        const current = useRunStore.getState().runState;
        useRunStore.getState().setRunState(mergeRunSummary(current, run));
      }

      logger.info('RunStreamService: REST says still running, next poll with backoff');
      this._scheduleNextStalePoll();
    } catch (err) {
      logger.warn('RunStreamService: REST poll failed, retrying with backoff', { error: err.message });
      this._scheduleNextStalePoll();
    }
  }

  // ── Final Completion Check ─────────────────────────────────────

  /**
   * Called when max stale polls is reached. Performs one final REST check
   * and if the run is still running, schedules a slow background poll (2 min).
   */
  async _finalCompletionCheck(meta) {
    try {
      const { TestService } = await import('../middleware/services/TestService');
      const userData = useUserStore.getState().userData;
      const run = await TestService.getRun(
        meta.projectId, meta.runnableId, meta.runId, userData, meta.tenantId,
        { bypassCache: true }
      );

      if (run?.testResults?.testSummary?.runStatus === 'COMPLETED' ||
          run?.result === 'PASSED' || run?.result === 'FAILED' || run?.result === 'ERROR') {
        logger.info('RunStreamService: final check confirms run completed');
        const current = useRunStore.getState().runState;
        const normalized = normalizeCompletedRunState(run);
        const currentPerms = current?.testResults?.permutations?.length || 0;
        const restPerms = normalized.testResults?.permutations?.length || 0;

        if (restPerms > currentPerms) {
          useRunStore.getState().setRunState(mergeRunSummary(current, normalized));
        } else {
          useRunStore.getState().setRunState(normalizeCompletedRunState(current));
        }
        useRunStore.getState().completeRun();
        this._destroyWorker();
        return;
      }

      // Still running — schedule one more slow background poll (2 min)
      logger.info('RunStreamService: still running after max polls, scheduling slow background poll');
      this._clearStaleTimer();
      this._staleTimer = setTimeout(() => {
        this._staleTimer = null;
        this._stalePollCount = 0; // reset so _handleStaleRun can do another round
        this._handleStaleRun();
      }, 120_000);
    } catch (err) {
      logger.warn('RunStreamService: final completion check failed', { error: err.message });
      // Schedule retry in 2 min
      this._clearStaleTimer();
      this._staleTimer = setTimeout(() => {
        this._staleTimer = null;
        this._stalePollCount = 0;
        this._handleStaleRun();
      }, 120_000);
    }
  }

  // ── Post-Completion Enrichment ─────────────────────────────────

  /**
   * After WS_COMPLETED, fetch the full run state from REST.
   * The WebSocket COMPLETED message often has a truncated permutations array
   * (e.g., 1 entry when the backend completes quickly). The REST API returns
   * the full permutation data needed by the RunDetailsPanel.
   *
   * @param {Object} meta - activeRunMeta captured before completeRun() cleared it
   */
  async _enrichCompletedRun(meta) {
    try {
      const { TestService } = await import('../middleware/services/TestService');
      const userData = useUserStore.getState().userData;

      const fullRun = await TestService.getRun(
        meta.projectId, meta.runnableId, meta.runId, userData, meta.tenantId,
        { bypassCache: true }
      );

      // Only update if the store still shows the same completed run
      const current = useRunStore.getState().runState;
      if (current?.runid !== meta.runId) return;

      if (fullRun) {
        const normalized = normalizeCompletedRunState(fullRun);
        const currentPerms = current.testResults?.permutations?.length || 0;
        const restPerms = normalized.testResults?.permutations?.length || 0;

        // Only update if REST data is actually richer (more permutations).
        // The REST response often has LESS detail than the WS payload
        // (no permutations array, no testSummary counts), so blindly
        // replacing would make the UI look broken.
        if (restPerms > currentPerms) {
          // Merge counts — REST may have more permutations but lack summary counts
          const enriched = mergeRunSummary(current, normalized);
          logger.info('RunStreamService: enriched completed run with full REST data', {
            currentPerms, restPerms,
          });
          useRunStore.getState().setRunState(enriched);
        } else {
          logger.debug('RunStreamService: REST data not richer, keeping WS data', {
            currentPerms, restPerms,
          });
        }
      }
    } catch (err) {
      // Non-critical — the truncated WS data is still usable
      logger.debug('RunStreamService: enrichment fetch failed', { error: err.message });
    }
  }

  // ── Frame Sequencing ────────────────────────────────────────────

  /**
   * Live-stream frame ordering guard.
   *
   * WS frames are pushed via async (fire-and-forget) Lambda invocation, which
   * guarantees neither ordering nor delivery. To make the UI resilient, the
   * backend stamps every frame with a monotonic per-run `sequence` (starts at 1,
   * +1 each frame). This keeps the highest sequence seen per run and discards
   * anything older, so a late-arriving older frame can't make the view jump back.
   *
   *   - sequence 0 / absent → "unstamped" (WebUI push path, S3 snapshot). Cannot
   *     be ordered, so always apply without moving the watermark.
   *   - sequence === 1 for a run we already track → the run restarted (backend
   *     resets its counter to 1); treat as a new run and reset tracking.
   *   - sequence <= highest applied → stale/reordered duplicate; discard.
   *   - sequence > highest + 1 → one or more frames were dropped in transit. The
   *     chunker never re-sends trimmed steps over the WS, so backfill from the
   *     authoritative S3 snapshot to fill the hole.
   *
   * Once a run is marked terminal (COMPLETED applied), every later progress frame
   * for it is discarded — async delivery means a straggler progress frame can
   * arrive (or even be stamped higher) after COMPLETED, and re-applying it would
   * roll the UI back from its terminal state. The terminal flag avoids assuming
   * COMPLETED carried the numerically-highest sequence.
   *
   * @returns {boolean} true if the frame should be applied, false to discard it.
   */
  _shouldApplyFrame(payload) {
    const runId = payload?.runid;
    const seq = payload?.sequence ?? 0;

    // Unstamped — can't be ordered. Always apply; don't touch the watermark.
    // (Snapshot/REST backfills are unstamped and are authoritative even
    //  post-completion, so they intentionally pass this guard.)
    if (!runId || seq === 0) return true;

    const prev = this._lastSeq.get(runId) ?? 0;

    // Counter restarted → genuine run reuse. Reset all per-run tracking (incl.
    // the terminal flag) and apply. Checked before the terminal guard so a
    // restart can revive a runId we'd previously marked complete.
    if (seq === 1 && prev !== 0) {
      logger.info('RunStreamService: sequence restarted at 1, treating as new run', { runId });
      this._terminal.delete(runId);
      this._lastSeq.set(runId, seq);
      return true;
    }

    // Run already completed → drop any straggler progress frame.
    if (this._terminal.has(runId)) {
      logger.debug('RunStreamService: dropping post-completion frame', { runId, seq });
      return false;
    }

    // Stale / reordered frame — discard.
    if (seq <= prev) {
      logger.debug('RunStreamService: dropping stale frame', { runId, seq, have: prev });
      return false;
    }

    // Gap in the sequence → a frame was dropped. Backfill from the S3 snapshot
    // (fire-and-forget; it's the complete, ordered state) before applying this one.
    // Guard on runId so a gap can never backfill a different run after a fast switch.
    if (prev !== 0 && seq > prev + 1) {
      logger.warn('RunStreamService: sequence gap detected, backfilling from snapshot', {
        runId, seq, prev,
      });
      const meta = useRunStore.getState().activeRunMeta;
      if (meta && meta.runId === runId) this._fetchAndApplySnapshot(meta);
    }

    this._lastSeq.set(runId, seq);
    return true;
  }

  /**
   * Mark a run terminal so later straggler progress frames are discarded.
   * Also bumps the watermark to the COMPLETED frame's sequence (belt-and-braces
   * for any path that consults the watermark directly).
   */
  _markTerminal(payload) {
    const runId = payload?.runid;
    if (!runId) return;
    this._terminal.add(runId);
    const seq = payload?.sequence ?? 0;
    if (seq) this._lastSeq.set(runId, Math.max(seq, this._lastSeq.get(runId) ?? 0));
  }

  // ── Message Routing ─────────────────────────────────────────────

  /**
   * Route worker messages to the run store.
   */
  /**
   * True when every permutation has reached a terminal state — PASSED or FAILED
   * (executed), or SKIPPED / DISABLED (never ran). Used as the "run looks finished
   * but no COMPLETED arrived" fallback trigger.
   */
  _allPermutationsAccountedFor(payload) {
    const testResults = payload?.testResults;
    if (!testResults?.testSummary) return false;
    const counts = resolveRunCounts(testResults.testSummary, testResults.permutations);
    if (counts.total <= 0) return false;
    return counts.executed + counts.skipped + counts.disabled >= counts.total;
  }

  _handleWorkerMessage(event) {
    const { type, payload, message, code, runid } = event.data;
    const store = useRunStore.getState();

    switch (type) {
      case 'WS_OPEN':
        logger.info('RunStreamService: WebSocket connected');
        store.setWorkerStatus('connected');
        this._reconnectAttempts = 0;
        this._resetStaleTimer();
        break;

      case 'WS_SUBSCRIBED':
        logger.info('RunStreamService: subscribed to run', { runid });
        break;

      case 'WS_MESSAGE': {
        this._wsMessageCount++;
        this._resetStaleTimer();
        if (store.workerStatus !== 'streaming') {
          store.setWorkerStatus('streaming');
        }

        // Frame sequencing — discard stale/reordered frames before applying.
        // A COMPLETED frame is terminal and authoritative, so always apply it
        // regardless of its sequence (run the ordering check on progress only).
        const incomingStatus = payload?.testResults?.testSummary?.runStatus;
        if (incomingStatus !== 'COMPLETED' && !this._shouldApplyFrame(payload)) {
          break;
        }

        // Merge counts so a COMPLETED message with zeroed fields
        // doesn't overwrite correct tallies from earlier progress messages.
        const mergedMsg = mergeRunSummary(store.runState, payload);
        store.setRunState(mergedMsg);

        const summary = mergedMsg?.testResults?.testSummary;

        // Redundant COMPLETED check — in case the worker's check failed
        if (summary?.runStatus === 'COMPLETED') {
          logger.info('RunStreamService: COMPLETED caught in WS_MESSAGE (worker missed it)');
          this._markTerminal(payload);
          const metaForEnrich = store.activeRunMeta;
          store.completeRun();
          this._clearStaleTimer();
          this._destroyWorker();
          if (metaForEnrich) this._enrichCompletedRun(metaForEnrich);
        }
        // Every permutation accounted for but no COMPLETED yet — poll REST in 2s
        // (backend sometimes closes WS without sending COMPLETED).
        //
        // "Accounted for" is executed + skipped + disabled, not just executed: a run
        // with disabled or all-steps-disabled permutations never reaches
        // executed === total, so comparing those two would leave it waiting forever.
        // GUARD: Only check after receiving at least 3 messages to avoid
        // false positives from early catch-up messages on fast re-runs.
        else if (this._wsMessageCount >= 3 && this._allPermutationsAccountedFor(mergedMsg)) {
          this._clearStaleTimer();
          this._staleTimer = setTimeout(() => {
            this._staleTimer = null;
            this._handleStaleRun();
          }, 2000);
        }
        break;
      }

      case 'WS_COMPLETED': {
        this._clearStaleTimer();
        this._markTerminal(payload);

        // Capture meta BEFORE completeRun() clears activeRunMeta
        const meta = store.activeRunMeta;

        // Merge testSummary counts from the progress state — the COMPLETED
        // message may arrive with zeroed counts while earlier progress
        // messages had the correct tallies.
        const merged = mergeRunSummary(store.runState, payload);
        const cs = merged?.testResults?.testSummary;
        logger.info('RunStreamService: WS_COMPLETED received', {
          runStatus: cs?.runStatus,
          numberOfPermutations: cs?.numberOfPermutations,
          numberOfPermutationsPassed: cs?.numberOfPermutationsPassed,
          numberOfPermutationsFailed: cs?.numberOfPermutationsFailed,
          permutationCount: merged?.testResults?.permutations?.length ?? 'n/a',
          msgCountBeforeComplete: this._wsMessageCount,
        });
        store.setRunState(merged);
        store.completeRun();
        this._destroyWorker();

        // The WS COMPLETED message may have a truncated permutations array
        // (e.g., 1 entry instead of 50 when the backend completes fast).
        // Fetch the full run state from REST to get complete permutation data.
        if (meta) this._enrichCompletedRun(meta);
        break;
      }

      case 'WS_ERROR':
        logger.error('RunStreamService: WebSocket error', { message });
        // Don't set error immediately — try to reconnect first
        if (store.activeRunMeta) {
          this._scheduleReconnect();
        } else {
          store.setError(message);
        }
        break;

      case 'WS_CLOSED':
        logger.info('RunStreamService: WebSocket closed', { code });
        // Normal close (1000) after COMPLETED is handled by WS_COMPLETED.
        // Abnormal close while run is active → reconnect.
        if (code !== 1000 && store.activeRunMeta) {
          this._scheduleReconnect();
        }
        break;

      default:
        break;
    }
  }

  // ── S3 Snapshot Backfill ────────────────────────────────────────

  /**
   * Fetch the S3 stream.json snapshot and apply it to the run store.
   * Called at the start of connect() before the WebSocket worker is created.
   *
   * Fast runs complete before the WebSocket is established; the snapshot is
   * the authoritative final state in that case. Returns the runStatus string
   * from the snapshot ('COMPLETED', 'IN_PROGRESS'), or null if the snapshot
   * is unavailable (new run not yet written, network error, missing meta).
   */
  async _fetchAndApplySnapshot(meta) {
    if (!meta.logPath || !meta.projectName) return null;

    try {
      const { TestService } = await import('../middleware/services/TestService');
      const userData = useUserStore.getState().userData;
      const snapshot = await TestService.getRunSnapshot(
        meta.projectId, meta.logPath, meta.projectName, meta.runId,
        userData, meta.tenantId
      );

      if (!snapshot?.testResults?.testSummary) return null;

      const current = useRunStore.getState().runState;
      const merged = mergeRunSummary(current, snapshot);
      useRunStore.getState().setRunState(merged);

      const status = snapshot.testResults.testSummary.runStatus;
      logger.info('RunStreamService: S3 snapshot applied', { runId: meta.runId, status });
      return status || null;
    } catch (err) {
      // 404 = snapshot not written yet (run just started) — proceed with WS
      logger.debug('RunStreamService: snapshot not available, proceeding with WS', {
        runId: meta.runId,
        error: err.message,
      });
      return null;
    }
  }

  // ── Public API ──────────────────────────────────────────────────

  /**
   * Start streaming a new run via the Web Worker.
   * Fetches the S3 snapshot first (standard "snapshot + delta" pattern):
   *   - COMPLETED in snapshot → apply it and skip WS (fast-run race condition)
   *   - IN_PROGRESS in snapshot → apply as starting state, then open WS
   *   - Snapshot absent (new run) → open WS directly
   *
   * @param {Object} meta - { runId, webSocketUrl, projectId, runnableId, logPath, projectName }
   */
  async connect(meta) {
    // Clean up any previous run
    this.disconnect();
    this._reconnectAttempts = 0;
    this._stalePollCount = 0;
    this._wsMessageCount = 0;
    this._lastSeq.clear(); // reset frame-sequence watermark for the new run
    this._terminal.clear(); // clear completed-run flags for the new run

    // Capture id AFTER disconnect() increments it so any in-flight snapshot
    // from a prior connect() is discarded once this id advances.
    const connectId = this._connectId;

    useRunStore.getState().setWorkerStatus('connecting');
    logger.info('RunStreamService: connecting to run stream', { runId: meta.runId });

    // Snapshot backfill — must happen before worker creation so a COMPLETED
    // snapshot short-circuits without ever opening the WebSocket.
    const snapshotStatus = await this._fetchAndApplySnapshot(meta);
    if (connectId !== this._connectId) return; // superseded by a newer connect/disconnect

    if (snapshotStatus === 'COMPLETED') {
      logger.info('RunStreamService: S3 snapshot shows completed run, skipping WS', {
        runId: meta.runId,
      });
      useRunStore.getState().completeRun();
      return;
    }

    this._ensureWorker();
    this._worker.postMessage({
      type: 'CONNECT',
      webSocketUrl: meta.webSocketUrl,
      runId: meta.runId,
      projectId: meta.projectId,
      runnableId: meta.runnableId,
    });
  }

  /**
   * Gracefully disconnect and tear down the worker.
   */
  disconnect() {
    this._connectId++; // invalidate any in-flight snapshot fetch from connect()
    this._clearReconnectTimer();
    this._clearStaleTimer();

    if (this._worker) {
      try {
        this._worker.postMessage({ type: 'DISCONNECT' });
      } catch {
        // Worker may already be terminated
      }
      this._destroyWorker();
    }

    useRunStore.getState().setWorkerStatus('idle');
  }

  // ── Reconnection Logic ──────────────────────────────────────────

  /**
   * Schedule a reconnection attempt with exponential backoff.
   */
  _scheduleReconnect() {
    if (this._reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      logger.error('RunStreamService: max reconnect attempts reached');
      useRunStore.getState().setError(
        'Connection lost. The run may still be executing — check Results for the final status.'
      );
      useRunStore.getState().setWorkerStatus('disconnected');
      return;
    }

    const delay = RECONNECT_DELAYS[this._reconnectAttempts] || 15000;
    this._reconnectAttempts++;

    logger.info('RunStreamService: scheduling reconnect', {
      attempt: this._reconnectAttempts,
      delay,
    });

    useRunStore.getState().setWorkerStatus('reconnecting');

    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      this.reconnect();
    }, delay);
  }

  /**
   * Clear any pending reconnection timer.
   */
  _clearReconnectTimer() {
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
  }

  /**
   * Reconnect to an in-progress run.
   * ALWAYS checks REST API first to see if the run already completed.
   * Only falls back to WebSocket if REST confirms still running.
   *
   * @param {Object} [userData] - Auth context (optional — interceptors handle auth)
   */
  async reconnect(userData) {
    const genAtStart = this._generation;
    const { activeRunMeta } = useRunStore.getState();
    if (!activeRunMeta) {
      logger.debug('RunStreamService: no active run to reconnect');
      return;
    }

    // Always REST-check first (auth handled by axios interceptors)
    try {
      const { TestService } = await import('../middleware/services/TestService');

      // Bail if a newer connect() was called while we were awaiting
      if (genAtStart !== this._generation) return;

      const effectiveUserData = userData || useUserStore.getState().userData;
      const run = await TestService.getRun(
        activeRunMeta.projectId,
        activeRunMeta.runnableId,
        activeRunMeta.runId,
        effectiveUserData,
        activeRunMeta.tenantId,
        { bypassCache: true }
      );

      // Bail if a newer connect() was called while we were awaiting
      if (genAtStart !== this._generation) return;

      // Run is already completed — no need for WebSocket
      if (run?.testResults?.testSummary?.runStatus === 'COMPLETED' ||
          run?.result === 'PASSED' || run?.result === 'FAILED' || run?.result === 'ERROR') {
        logger.info('RunStreamService: run already completed, skipping WS');
        const current = useRunStore.getState().runState;
        const normalized = normalizeCompletedRunState(run);
        const currentPerms = current?.testResults?.permutations?.length || 0;
        const restPerms = normalized.testResults?.permutations?.length || 0;

        if (restPerms > currentPerms) {
          useRunStore.getState().setRunState(mergeRunSummary(current, normalized));
        } else {
          useRunStore.getState().setRunState(normalizeCompletedRunState(current));
        }
        useRunStore.getState().completeRun();
        this._destroyWorker();
        return;
      }
    } catch (err) {
      // Bail if a newer connect() was called while we were awaiting
      if (genAtStart !== this._generation) return;

      // 404 → run no longer exists
      if (err.response?.status === 404) {
        logger.info('RunStreamService: run not found, clearing state');
        useRunStore.getState().clearActiveRun();
        return;
      }
      // Other errors → fall through to WebSocket reconnect
      logger.warn('RunStreamService: REST check failed, trying WebSocket', {
        error: err.message,
      });
    }

    // Final generation check before touching the worker
    if (genAtStart !== this._generation) return;

    // Reconnect via WebSocket
    this._destroyWorker();
    this._ensureWorker();
    this._worker.postMessage({
      type: 'CONNECT',
      webSocketUrl: activeRunMeta.webSocketUrl,
      runId: activeRunMeta.runId,
      projectId: activeRunMeta.projectId,
      runnableId: activeRunMeta.runnableId,
    });

    useRunStore.getState().setWorkerStatus('reconnecting');
    logger.info('RunStreamService: reconnecting to run stream', {
      runId: activeRunMeta.runId,
    });
  }

  /**
   * Check for a persisted active run on app init and reconnect if needed.
   * Called once after Zustand hydration completes.
   *
   * @param {Object} [userData] - Auth context for REST fallback
   */
  async checkAndReconnect(userData) {
    const { activeRunMeta } = useRunStore.getState();
    if (!activeRunMeta) return;

    // Check if the run is stale (> 2 hours old)
    const startedAt = new Date(activeRunMeta.startedAt).getTime();
    if (Date.now() - startedAt > STALE_RUN_THRESHOLD_MS) {
      logger.info('RunStreamService: stale run detected, checking via REST');

      if (userData) {
        try {
          const { TestService } = await import('../middleware/services/TestService');
          const run = await TestService.getRun(
            activeRunMeta.projectId,
            activeRunMeta.runnableId,
            activeRunMeta.runId,
            userData,
            activeRunMeta.tenantId
          );

          if (run) {
            useRunStore.getState().setRunState(normalizeCompletedRunState(run));
            useRunStore.getState().completeRun();
          } else {
            useRunStore.getState().clearActiveRun();
          }
        } catch {
          useRunStore.getState().clearActiveRun();
        }
      } else {
        useRunStore.getState().clearActiveRun();
      }
      return;
    }

    // Fresh enough — try to reconnect
    await this.reconnect(userData);
  }
}

export const runStreamService = new RunStreamService();